import { useState, useMemo, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format, isValid, parseISO, getYear, getMonth } from "date-fns";
import { sv, enUS, type Locale } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { VehicleTest } from "@/types/vehicleTest";
import { VehicleDetails } from "@/types/vehicleDetails";
import { useTest } from "@/hooks/useTest";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Hash,
  Car,
  Calendar as CalendarIcon,
  Settings,
  Gauge,
  Users,
  Key,
  MapPin,
  FileText,
  Zap,
  Gauge as EngineIcon,
  Leaf,
  AlertTriangle,
  Palette,
  Fuel,
  DoorOpen,
  Clock,
  Sofa,
  ChevronDown,
} from "lucide-react";
import { EquipmentSection } from "@/components/valuation/form-sections/EquipmentSection";
import { useTheme } from "@/context/ThemeContext";
import { MakeSelectionDialog } from "@/admin/car-card/dialogs/MakeSelectionDialog";
import { ModelSelectionDialog } from "@/admin/car-card/dialogs/ModelSelectionDialog";
import { EngineSelectionDialog } from "@/admin/car-card/dialogs/EngineSelectionDialog";
import {
  fuelTypeLabels,
  transmissionTypeLabels,
  labelToFuelType,
  labelToFuelTypeAnyLanguage,
  labelToTransmissionAnyLanguage,
  labelToTransmission,
  getColorLabel,
  getBodyTypeLabel,
} from "@/i18n/labels/vehicleLabels";
import { TransmissionType, VehicleFuelType } from "@/types/enums";
import { getLanguage } from "@/utils/languageUtils";
import { useLanguage } from "@/context/LanguageContext";
import { carTestLabels } from "@/i18n/labels";
import { useNativePlatform } from "@/hooks/useNativePlatform";


const iconStyle = "w-4 h-4 md:w-5 md:h-5 flex-shrink-0";

const POPULAR_COUNTRY_KEYS = [
  "Sverige",
  "Tyskland",
  "Frankrike",
  "Italien",
  "Spanien",
  "Storbritannien",
  "Norge",
  "Danmark",
  "Finland",
  "Nederländerna",
  "Belgien",
  "Österrike",
  "Schweiz",
  "Polen",
  "Tjeckien",
  "Ungern",
  "Japan",
  "USA",
  "Kina",
  "Sydkorea",
];

const getMonthNames = (locale: Locale) =>
  Array.from({ length: 12 }, (_, i) =>
    format(new Date(2000, i, 1), "MMMM", { locale }),
  );

const MOCK_FUEL_TYPES = [
  "Bensin",
  "Diesel",
  "El",
  "Hybrid",
  "Laddhybrid",
  "Gas",
  "Etanol",
  "Annat",
];
const MOCK_BODY_TYPES = [
  "Sedan",
  "Kombi",
  "SUV",
  "Halvkombi",
  "Coupe",
  "Cabriolet",
  "Minibuss",
  "Pickup",
];
const MOCK_TRANSMISSIONS = ["Automat", "Manuell", "Halvautomatisk", "CVT"];

const MOCK_COLORS = [
  "Svart",
  "Vit",
  "Silver",
  "Blå",
  "Röd",
  "Grå",
  "Grön",
  "Brun",
  "Guld",
  "Beige",
];

const ADDITIONAL_COLORS = [
  "Lila",
  "Orange",
  "Rosa",
  "Turkos",
  "Burgundy",
  "Koppar",
  "Grön metallisk",
  "Blå metallisk",
  "Röd metallisk",
  "Svart metallisk",
  "Vit metallisk",
  "Silver metallisk",
];

interface VerifyVehicleInfoFormProps {
  test: VehicleTest;
  onClose: () => void;
}

function isElectric(d: Partial<VehicleDetails>) {
  const f = (d?.fuelType ?? "").toLowerCase();
  return f === "el" || f === "electric" || f === "elbil";
}

export const VerifyVehicleInfoForm = ({
  test,
  onClose,
}: VerifyVehicleInfoFormProps) => {
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { isNative } = useNativePlatform();
  const { updateTest, isUpdatingTest } = useTest();

  const [vehicleDetails, setVehicleDetails] = useState<Partial<VehicleDetails>>(
    () => {
      const vd: Partial<VehicleDetails> = test.vehicleDetails ?? {};
      return {
        ...vd,
        equipment: vd.equipment ?? [],
        regNumber: vd.regNumber ?? test.regNumber ?? "",
        vin: vd.vin ?? test.vin ?? "",
      };
    },
  );
  const [crashProblem, setCrashProblem] = useState<"yes" | "no" | "unknown">(
    (test.vehicleTestData?.crash?.problem as "yes" | "no" | "unknown") ??
      "unknown",
  );
  const [equipment, setEquipment] = useState<string[]>(
    test.vehicleDetails?.equipment ?? [],
  );
  const [makeDialogOpen, setMakeDialogOpen] = useState(false);
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [engineDialogOpen, setEngineDialogOpen] = useState(false);
  const [lastInspectionDateOpen, setLastInspectionDateOpen] = useState(false);
  const [fuelTypeDialogOpen, setFuelTypeDialogOpen] = useState(false);
  const [transmissionDialogOpen, setTransmissionDialogOpen] = useState(false);
  const [bodyTypeDialogOpen, setBodyTypeDialogOpen] = useState(false);
  const [colorDialogOpen, setColorDialogOpen] = useState(false);
  const [showAllColors, setShowAllColors] = useState(false);
  const [colorOtherText, setColorOtherText] = useState("");
  const [showColorOtherInput, setShowColorOtherInput] = useState(false);
  const [originCustomValue, setOriginCustomValue] = useState("");
  const [showOriginCustomInput, setShowOriginCustomInput] = useState(false);

  // Calendar state for "Besiktas senast"
  const getInitialDate = () => {
    const raw = test.vehicleDetails?.lastInspectionDate;
    return raw && typeof raw === "string" && raw.trim() ? parseISO(raw) : null;
  };
  const initialDate = getInitialDate();
  const [selectedMonth, setSelectedMonth] = useState<number>(
    initialDate ? getMonth(initialDate) : new Date().getMonth(),
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    initialDate ? getYear(initialDate) : new Date().getFullYear(),
  );

  const electric = useMemo(() => isElectric(vehicleDetails), [vehicleDetails]);
  const vehicleType = (vehicleDetails.vehicleType ?? "car") as "car" | "mc";

  // Update selected month/year when lastInspectionDate changes
  useEffect(() => {
    const raw = vehicleDetails.lastInspectionDate;
    const d =
      raw && typeof raw === "string" && raw.trim() ? parseISO(raw) : null;
    if (d && isValid(d)) {
      setSelectedMonth(getMonth(d));
      setSelectedYear(getYear(d));
    }
  }, [vehicleDetails.lastInspectionDate]);

  // Initialize origin custom input state
  useEffect(() => {
    const originValue = vehicleDetails.origin;
    if (originValue && !POPULAR_COUNTRY_KEYS.includes(originValue)) {
      setShowOriginCustomInput(true);
      setOriginCustomValue(originValue);
    } else {
      setShowOriginCustomInput(false);
      setOriginCustomValue("");
    }
  }, [vehicleDetails.origin]);

  // Initialize color other input state
  useEffect(() => {
    const colorValue = vehicleDetails.color;
    if (
      colorValue &&
      !MOCK_COLORS.includes(colorValue) &&
      !ADDITIONAL_COLORS.includes(colorValue)
    ) {
      setShowColorOtherInput(true);
      setColorOtherText(colorValue);
    } else {
      setShowColorOtherInput(false);
      setColorOtherText("");
    }
  }, [vehicleDetails.color]);

  const handleColorOtherClick = () => {
    setShowColorOtherInput(true);
    setColorOtherText(vehicleDetails.color || "");
  };

  const handleColorOtherSubmit = () => {
    if (colorOtherText.trim()) {
      update("color", colorOtherText.trim());
      setColorDialogOpen(false);
    }
  };

  const update = (
    key: keyof VehicleDetails,
    value: string | number | undefined,
  ) => {
    setVehicleDetails((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      const vd: Partial<VehicleDetails> = {
        ...test.vehicleDetails,
        ...vehicleDetails,
        equipment: equipment.length
          ? equipment
          : (test.vehicleDetails?.equipment ?? []),
      };
      // Ensure numeric fields are numbers where expected
      if (vd.year != null) vd.year = Number(vd.year);
      if (vd.mileage != null) vd.mileage = Number(vd.mileage);
      if (vd.seats != null) vd.seats = Number(vd.seats);
      if (vd.owners != null) vd.owners = Number(vd.owners);
      if (vd.keys != null) vd.keys = Number(vd.keys);
      if (vd.doors != null) vd.doors = Number(vd.doors);
      if (vd.horsepower != null) vd.horsepower = String(vd.horsepower);
      if (vd.electricDriveRange != null)
        vd.electricDriveRange = Number(vd.electricDriveRange);
      if (vd.annualVehicleTax != null) {
        const n = Number(vd.annualVehicleTax);
        vd.annualVehicleTax = !isNaN(n) && n >= 0 ? Math.floor(n) : undefined;
      } else {
        (vd as Record<string, unknown>).annualVehicleTax = null;
      }

      // Convert Swedish labels to technical enum values for backend
      const lang = getLanguage();
      // Same language-switch race as the vehicle-card editor: reverse against both locales.
      const fuelTypeReverseMap = labelToFuelTypeAnyLanguage();
      const transmissionReverseMap = labelToTransmissionAnyLanguage();

      if (vd.fuelType) {
        const technicalFuelType =
          fuelTypeReverseMap[vd.fuelType] || vd.fuelType;
        vd.fuelType = technicalFuelType as string;
      }

      if (vd.transmission) {
        const technicalTransmission =
          transmissionReverseMap[vd.transmission] || vd.transmission;
        vd.transmission = technicalTransmission as string;
      }

      const vehicleName =
        [vd.make, vd.model].filter(Boolean).join(" ").trim() ||
        test.vehicleName;

      // Mirrors the updateData literal below exactly (full submitted payload).
      await updateTest({
        testId: test.id,
        updateData: {
          vehicleName,
          vehicleDetails: vd,
          vehicleTestData: {
            ...(test.vehicleTestData ?? {}),
            crash: {
              ...(test.vehicleTestData?.crash ?? {}),
              problem: crashProblem,
            },
          },
          price: test.price,
          comments: test.comments,
          vatCar: test.vatCar,
          customerId: test.customerId,
        },
      });

      toast.success("Ändringar sparade i fordonstestet.");
      onClose();
    } catch (e) {
      toast.error(
        ct.dialogs.verifyVehicleInfo.errors.saveError ||
          "Kunde inte spara ändringar.",
      );
    }
  };

  const labelClass = isDark ? "text-white/60" : "text-gray-500";
  const inputClass = "h-9 text-sm";

  const dateLocale = language === "en" ? enUS : sv;
  const monthNames = useMemo(() => getMonthNames(dateLocale), [dateLocale]);

  // Format transmission and fuel type for display (supports enum and Swedish values)
  const formatTransmission = (transmission?: string): string => {
    if (!transmission)
      return ct.dialogs.verifyVehicleInfo.placeholders.notSpecified;
    const transmissionLabels = transmissionTypeLabels(language);
    // Convert to uppercase for enum lookup
    const upperKey = transmission.toUpperCase().replace(/-/g, "_");
    // Try direct lookup with enum value first
    if (upperKey in TransmissionType) {
      const enumKey = upperKey as TransmissionType;
      if (transmissionLabels[enumKey]) return transmissionLabels[enumKey];
    }
    // Try mapping common values (lowercase input to enum)
    const mapping: Record<string, string> = {
      manual: transmissionLabels[TransmissionType.MANUAL],
      automatic: transmissionLabels[TransmissionType.AUTOMATIC],
      semi_automatic: transmissionLabels[TransmissionType.SEMI_AUTOMATIC],
      cvt: transmissionLabels[TransmissionType.CVT],
      other: transmissionLabels[TransmissionType.OTHER],
      manuell: transmissionLabels[TransmissionType.MANUAL],
      automat: transmissionLabels[TransmissionType.AUTOMATIC],
      halvautomatisk: transmissionLabels[TransmissionType.SEMI_AUTOMATIC],
      halvautomat: transmissionLabels[TransmissionType.SEMI_AUTOMATIC],
    };
    const lowerKey = transmission.toLowerCase().replace(/_/g, "_");
    return mapping[lowerKey] || transmission;
  };

  const formatFuelType = (fuelType?: string): string => {
    if (!fuelType)
      return ct.dialogs.verifyVehicleInfo.placeholders.notSpecified;
    const fuelLabels = fuelTypeLabels(language);
    // Convert to uppercase for enum lookup
    const upperKey = fuelType.toUpperCase().replace(/-/g, "_");
    // Try direct lookup with enum value first
    if (upperKey in VehicleFuelType) {
      const enumKey = upperKey as VehicleFuelType;
      if (fuelLabels[enumKey]) return fuelLabels[enumKey];
    }
    // Try mapping common values (lowercase input to enum)
    const mapping: Record<string, string> = {
      gasoline: fuelLabels[VehicleFuelType.GASOLINE],
      diesel: fuelLabels[VehicleFuelType.DIESEL],
      electric: fuelLabels[VehicleFuelType.ELECTRIC],
      hybrid: fuelLabels[VehicleFuelType.HYBRID],
      plug_in_hybrid: fuelLabels[VehicleFuelType.PLUG_IN_HYBRID],
      other: fuelLabels[VehicleFuelType.OTHER],
      bensin: fuelLabels[VehicleFuelType.GASOLINE],
      el: fuelLabels[VehicleFuelType.ELECTRIC],
      hybrid_: fuelLabels[VehicleFuelType.HYBRID],
      laddhybrid: fuelLabels[VehicleFuelType.PLUG_IN_HYBRID],
      // Both used to render as "Annat" because the frontend enum had no member for them.
      gas: fuelLabels[VehicleFuelType.GAS],
      etanol: fuelLabels[VehicleFuelType.ETHANOL],
      ethanol: fuelLabels[VehicleFuelType.ETHANOL],
      annat: fuelLabels[VehicleFuelType.OTHER],
    };
    const lowerKey = fuelType.toLowerCase().replace(/_/g, "_");
    return mapping[lowerKey] || fuelType;
  };

  const handleMakeSelect = (makeName: string) => {
    const prevMake = vehicleDetails.make;
    setVehicleDetails((prev) => ({
      ...prev,
      make: makeName,
      ...(prevMake !== makeName ? { model: "", engine: "" } : {}),
    }));
    setMakeDialogOpen(false);
  };

  const basicFields: {
    label: string;
    key: keyof VehicleDetails;
    type: "text" | "number";
    icon: React.ReactNode;
    disabled?: boolean;
  }[] = [
    {
      // Identity of the vehicle - shown for reference but not editable here. Both keep
      // their stored values and still go out unchanged in the save payload.
      label: ct.dialogs.verifyVehicleInfo.labels.regNr,
      key: "regNumber",
      type: "text",
      icon: <Hash className={iconStyle} />,
      disabled: true,
    },
    {
      label: ct.dialogs.verifyVehicleInfo.labels.vin,
      key: "vin",
      type: "text",
      icon: <Car className={iconStyle} />,
      disabled: true,
    },
    {
      label: ct.dialogs.verifyVehicleInfo.labels.variant,
      key: "variant",
      type: "text",
      icon: <Car className={iconStyle} />,
    },
    {
      // Trim is stored separately from variant; each saves 1:1 to its own column and the
      // combined headline is only assembled at estimation time, never shown or stored.
      label: ct.dialogs.verifyVehicleInfo.labels.trim,
      key: "trim",
      type: "text",
      icon: <Car className={iconStyle} />,
    },
    {
      label: ct.dialogs.verifyVehicleInfo.labels.year,
      key: "year",
      type: "number",
      icon: <CalendarIcon className={iconStyle} />,
    },
    {
      label: ct.dialogs.verifyVehicleInfo.labels.mileage,
      key: "mileage",
      type: "number",
      icon: <Gauge className={iconStyle} />,
    },
    {
      label: ct.dialogs.verifyVehicleInfo.labels.seats,
      key: "seats",
      type: "number",
      icon: <Users className={iconStyle} />,
    },
  ];

  const additionalFields: {
    label: string;
    key: keyof VehicleDetails;
    type: "text" | "number";
    icon: React.ReactNode;
  }[] = [
    {
      label: ct.dialogs.verifyVehicleInfo.labels.owners,
      key: "owners",
      type: "number",
      icon: <Users className={iconStyle} />,
    },
    {
      label: ct.dialogs.verifyVehicleInfo.labels.keys,
      key: "keys",
      type: "number",
      icon: <Key className={iconStyle} />,
    },
    {
      label: ct.dialogs.verifyVehicleInfo.labels.annualTax,
      key: "annualVehicleTax",
      type: "number",
      icon: <FileText className={iconStyle} />,
    },
    {
      label: ct.dialogs.verifyVehicleInfo.labels.horsepower,
      key: "horsepower",
      type: "number",
      icon: <Zap className={iconStyle} />,
    },
    ...(electric
      ? [
          {
            label: ct.dialogs.verifyVehicleInfo.labels.electricRange,
            key: "electricDriveRange" as keyof VehicleDetails,
            type: "number" as const,
            icon: <Zap className={iconStyle} />,
          },
        ]
      : []),
    ...(!electric
      ? [
          {
            label: ct.dialogs.verifyVehicleInfo.labels.engineVolume,
            key: "engineVolume" as keyof VehicleDetails,
            type: "text" as const,
            icon: <EngineIcon className={iconStyle} />,
          },
        ]
      : []),
    {
      label: ct.dialogs.verifyVehicleInfo.labels.emissionClass,
      key: "emissionClass",
      type: "text",
      icon: <Leaf className={iconStyle} />,
    },
    {
      label: ct.dialogs.verifyVehicleInfo.labels.doors,
      key: "doors",
      type: "number",
      icon: <DoorOpen className={iconStyle} />,
    },
    {
      label: ct.dialogs.verifyVehicleInfo.labels.upholstery,
      key: "upholstery",
      type: "text",
      icon: <Sofa className={iconStyle} />,
    },
  ];

  const renderField = (item: {
    label: string;
    key: keyof VehicleDetails;
    type: "text" | "number";
    icon: React.ReactNode;
    disabled?: boolean;
  }) => {
    const raw = vehicleDetails[item.key];
    const val = raw === undefined || raw === null ? "" : String(raw);
    return (
      <div key={item.key} className="flex items-center gap-3">
        <div className={isDark ? "text-white" : "text-gray-900"}>
          {item.icon}
        </div>
        <div className="flex flex-col min-w-0 flex-1 gap-1">
          <label className={`text-xs md:text-sm ${labelClass}`}>
            {item.label}
          </label>
          <Input
            className={inputClass}
            type={item.type}
            value={val}
            disabled={item.disabled}
            onChange={(e) => {
              const v = e.target.value;
              if (item.type === "number") {
                const n = v === "" ? undefined : Number(v);
                update(item.key, n !== undefined && !isNaN(n) ? n : undefined);
              } else {
                update(item.key, v === "" ? "" : v);
              }
            }}
            placeholder={ct.dialogs.verifyVehicleInfo.placeholders.notSpecified}
          />
        </div>
      </div>
    );
  };

  return (
    <Card className="glass-card p-4 md:p-6 space-y-6">
      {/* Fordonsinformation */}
      <div className="space-y-4">
        <h3 className="text-base md:text-xl font-semibold pb-2">
          {ct.dialogs.verifyVehicleInfo.vehicleInfo}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            <div className={isDark ? "text-white" : "text-gray-900"}>
              <Car className={iconStyle} />
            </div>
            <div className="flex flex-col min-w-0 flex-1 gap-1">
              <label className={`text-xs md:text-sm ${labelClass}`}>
                {ct.dialogs.verifyVehicleInfo.labels.make}
              </label>
              <Button
                variant="outline"
                size="sm"
                className={`${inputClass} justify-start font-normal`}
                onClick={() => setMakeDialogOpen(true)}
              >
                {vehicleDetails.make ||
                  ct.dialogs.verifyVehicleInfo.placeholders.selectMake}
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={isDark ? "text-white" : "text-gray-900"}>
              <Car className={iconStyle} />
            </div>
            <div className="flex flex-col min-w-0 flex-1 gap-1">
              <label className={`text-xs md:text-sm ${labelClass}`}>
                {ct.dialogs.verifyVehicleInfo.labels.model}
              </label>
              <Button
                variant="outline"
                size="sm"
                className={`${inputClass} justify-start font-normal`}
                onClick={() => {
                  if (!vehicleDetails.make) {
                    toast.error(
                      ct.dialogs.verifyVehicleInfo.errors.selectMakeFirst,
                    );
                    return;
                  }
                  setModelDialogOpen(true);
                }}
                disabled={!vehicleDetails.make}
              >
                {vehicleDetails.model ||
                  ct.dialogs.verifyVehicleInfo.placeholders.selectModel}
              </Button>
            </div>
          </div>
          {basicFields.map(renderField)}

          {/* Färg */}
          <div className="flex items-center gap-3">
            <div className={isDark ? "text-white" : "text-gray-900"}>
              <Palette className={iconStyle} />
            </div>
            <div className="flex flex-col min-w-0 flex-1 gap-1">
              <label className={`text-xs md:text-sm ${labelClass}`}>
                {ct.dialogs.verifyVehicleInfo.labels.color}
              </label>
              <Button
                variant="outline"
                size="sm"
                className={`${inputClass} justify-start font-normal`}
                onClick={() => setColorDialogOpen(true)}
              >
                {getColorLabel(vehicleDetails.color, language) ||
                  ct.dialogs.verifyVehicleInfo.placeholders.notSpecified}
              </Button>
            </div>
          </div>

          {/* Drivmedel */}
          <div className="flex items-center gap-3">
            <div className={isDark ? "text-white" : "text-gray-900"}>
              <Fuel className={iconStyle} />
            </div>
            <div className="flex flex-col min-w-0 flex-1 gap-1">
              <label className={`text-xs md:text-sm ${labelClass}`}>
                {ct.dialogs.verifyVehicleInfo.labels.fuel}
              </label>
              <Button
                variant="outline"
                size="sm"
                className={`${inputClass} justify-start font-normal`}
                onClick={() => setFuelTypeDialogOpen(true)}
              >
                {formatFuelType(vehicleDetails.fuelType)}
              </Button>
            </div>
          </div>

          {/* Växellåda */}
          <div className="flex items-center gap-3">
            <div className={isDark ? "text-white" : "text-gray-900"}>
              <Settings className={iconStyle} />
            </div>
            <div className="flex flex-col min-w-0 flex-1 gap-1">
              <label className={`text-xs md:text-sm ${labelClass}`}>
                {ct.dialogs.verifyVehicleInfo.labels.transmission}
              </label>
              <Button
                variant="outline"
                size="sm"
                className={`${inputClass} justify-start font-normal`}
                onClick={() => setTransmissionDialogOpen(true)}
              >
                {formatTransmission(vehicleDetails.transmission)}
              </Button>
            </div>
          </div>

          {/* Chassityp */}
          {vehicleType !== "mc" && (
            <div className="flex items-center gap-3">
              <div className={isDark ? "text-white" : "text-gray-900"}>
                <Hash className={iconStyle} />
              </div>
              <div className="flex flex-col min-w-0 flex-1 gap-1">
                <label className={`text-xs md:text-sm ${labelClass}`}>
                  {ct.dialogs.verifyVehicleInfo.labels.bodyType}
                </label>
                <Button
                  variant="outline"
                  size="sm"
                  className={`${inputClass} justify-start font-normal`}
                  onClick={() => setBodyTypeDialogOpen(true)}
                >
                  {getBodyTypeLabel(vehicleDetails.bodyType, language) ||
                    ct.dialogs.verifyVehicleInfo.placeholders.notSpecified}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ytterligare information */}
      <div className="space-y-4">
        <h3 className="text-base md:text-xl font-semibold pb-2">
          {ct.dialogs.verifyVehicleInfo.additionalInfo}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {/* Ursprungsland - Special handling with dropdown + custom input */}
          <div className="flex items-center gap-3">
            <div className={isDark ? "text-white" : "text-gray-900"}>
              <MapPin className={iconStyle} />
            </div>
            <div className="flex flex-col min-w-0 flex-1 gap-1">
              <label className={`text-xs md:text-sm ${labelClass}`}>
                {ct.dialogs.verifyVehicleInfo.labels.origin}
              </label>
              {!showOriginCustomInput ? (
                <Select
                  value={vehicleDetails.origin || ""}
                  onValueChange={(value) => {
                    if (value === "custom") {
                      setShowOriginCustomInput(true);
                      setOriginCustomValue("");
                    } else {
                      update("origin", value);
                    }
                  }}
                >
                  <SelectTrigger className={inputClass}>
                    <SelectValue
                      placeholder={
                        ct.dialogs.verifyVehicleInfo.placeholders.selectCountry
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {POPULAR_COUNTRY_KEYS.map((countryKey) => {
                      const countries = (
                        ct.dialogs as { countries?: Record<string, string> }
                      ).countries;
                      const displayName = countries?.[countryKey] ?? countryKey;
                      return (
                        <SelectItem key={countryKey} value={countryKey}>
                          {displayName}
                        </SelectItem>
                      );
                    })}
                    <SelectItem value="custom">
                      {ct.dialogs.verifyVehicleInfo.placeholders.other ||
                        "Annan..."}
                    </SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={originCustomValue}
                    onChange={(e) => setOriginCustomValue(e.target.value)}
                    onBlur={() => {
                      update("origin", originCustomValue.trim() || undefined);
                      if (!originCustomValue.trim()) {
                        setShowOriginCustomInput(false);
                      }
                    }}
                    placeholder={
                      ct.dialogs.verifyVehicleInfo.placeholders.enterCountry ||
                      "Ange land"
                    }
                    className={inputClass}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    onClick={() => {
                      setShowOriginCustomInput(false);
                      setOriginCustomValue("");
                      update("origin", undefined);
                    }}
                  >
                    {ct.dialogs.verifyVehicleInfo.clear || "Rensa"}
                  </Button>
                </div>
              )}
            </div>
          </div>
          {additionalFields.map(renderField)}
          {!electric && (
            <div className="flex items-center gap-3">
              <div className={isDark ? "text-white" : "text-gray-900"}>
                <EngineIcon className={iconStyle} />
              </div>
              <div className="flex flex-col min-w-0 flex-1 gap-1">
                <label className={`text-xs md:text-sm ${labelClass}`}>
                  {ct.dialogs.verifyVehicleInfo.labels.engine}
                </label>
                <Button
                  variant="outline"
                  size="sm"
                  className={`${inputClass} justify-start font-normal`}
                  onClick={() => {
                    if (!vehicleDetails.make) {
                      toast.error(
                        ct.dialogs.verifyVehicleInfo.errors.selectMakeFirst,
                      );
                      return;
                    }
                    setEngineDialogOpen(true);
                  }}
                  disabled={!vehicleDetails.make}
                >
                  {vehicleDetails.engine ||
                    ct.dialogs.verifyVehicleInfo.placeholders.selectMake}
                </Button>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className={isDark ? "text-white" : "text-gray-900"}>
              <Clock className={iconStyle} />
            </div>
            <div className="flex flex-col min-w-0 flex-1 gap-1">
              <label className={`text-xs md:text-sm ${labelClass}`}>
                {ct.dialogs.verifyVehicleInfo.labels.lastInspection}
              </label>
              <div className="flex gap-2 items-center">
                {isNative ? (
                  <Input
                    type="date"
                    className={cn(inputClass, "flex-1")}
                    value={(() => {
                      const raw = vehicleDetails.lastInspectionDate;
                      const d =
                        raw && typeof raw === "string" && raw.trim()
                          ? parseISO(raw)
                          : null;
                      return d && isValid(d) ? format(d, "yyyy-MM-dd") : "";
                    })()}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) {
                        update("lastInspectionDate", "");
                        return;
                      }
                      const [y, m, day] = val.split("-").map(Number);
                      update(
                        "lastInspectionDate",
                        new Date(y, m - 1, day).toISOString(),
                      );
                    }}
                  />
                ) : (
                <Popover
                  modal={false}
                  open={lastInspectionDateOpen}
                  onOpenChange={setLastInspectionDateOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={cn(
                        `${inputClass} justify-start font-normal flex-1`,
                        !vehicleDetails.lastInspectionDate &&
                          "text-muted-foreground",
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {(() => {
                        const raw = vehicleDetails.lastInspectionDate;
                        const d =
                          raw && typeof raw === "string" && raw.trim()
                            ? parseISO(raw)
                            : null;
                        return d && isValid(d)
                          ? format(d, "yyyy-MM-dd")
                          : ct.dialogs.verifyVehicleInfo.placeholders
                              .selectDate;
                      })()}
                    </Button>
                  </PopoverTrigger>
                  {/* Above Dialog (z-220/221) — previous z-[100] hid the calendar behind the modal */}
                  <PopoverContent
                    className="w-auto p-0 min-w-[280px] sm:min-w-[320px] z-[240]"
                    align="start"
                    collisionPadding={16}
                  >
                    <div className="p-3">
                      {/* Month and Year Selectors */}
                      <div className="flex gap-2 mb-3">
                        <Select
                          value={selectedMonth.toString()}
                          onValueChange={(value) => {
                            const month = parseInt(value);
                            setSelectedMonth(month);
                            // Update date if one is selected
                            const raw = vehicleDetails.lastInspectionDate;
                            const d =
                              raw && typeof raw === "string" && raw.trim()
                                ? parseISO(raw)
                                : null;
                            if (d && isValid(d)) {
                              const daysInMonth = new Date(
                                selectedYear,
                                month + 1,
                                0,
                              ).getDate();
                              const day = Math.min(d.getDate(), daysInMonth);
                              const newDate = new Date(
                                selectedYear,
                                month,
                                day,
                              );
                              update(
                                "lastInspectionDate",
                                newDate.toISOString(),
                              );
                            }
                          }}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[250]">
                            {monthNames.map((month, index) => (
                              <SelectItem key={index} value={index.toString()}>
                                {month}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={selectedYear.toString()}
                          onValueChange={(value) => {
                            const year = parseInt(value);
                            setSelectedYear(year);
                            // Update date if one is selected
                            const raw = vehicleDetails.lastInspectionDate;
                            const d =
                              raw && typeof raw === "string" && raw.trim()
                                ? parseISO(raw)
                                : null;
                            if (d && isValid(d)) {
                              const daysInMonth = new Date(
                                year,
                                selectedMonth + 1,
                                0,
                              ).getDate();
                              const day = Math.min(d.getDate(), daysInMonth);
                              const newDate = new Date(
                                year,
                                selectedMonth,
                                day,
                              );
                              update(
                                "lastInspectionDate",
                                newDate.toISOString(),
                              );
                            }
                          }}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-[200px] z-[250]">
                            {Array.from(
                              { length: new Date().getFullYear() + 10 - 1899 },
                              (_, i) => 1900 + i,
                            )
                              .reverse()
                              .map((year) => (
                                <SelectItem key={year} value={year.toString()}>
                                  {year}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Calendar
                        mode="single"
                        locale={dateLocale}
                        selected={(() => {
                          const raw = vehicleDetails.lastInspectionDate;
                          const d =
                            raw && typeof raw === "string" && raw.trim()
                              ? parseISO(raw)
                              : null;
                          return d && isValid(d) ? d : undefined;
                        })()}
                        onSelect={(d) => {
                          if (d) {
                            update("lastInspectionDate", d.toISOString());
                            setSelectedMonth(getMonth(d));
                            setSelectedYear(getYear(d));
                            setLastInspectionDateOpen(false);
                          }
                        }}
                        month={new Date(selectedYear, selectedMonth)}
                        onMonthChange={(month) => {
                          setSelectedMonth(getMonth(month));
                          setSelectedYear(getYear(month));
                        }}
                        initialFocus
                        captionLayout="dropdown"
                        fromYear={1900}
                        toYear={new Date().getFullYear() + 10}
                        classNames={{
                          caption: "hidden", // Hide default caption since we have custom selects
                        }}
                      />
                    </div>
                  </PopoverContent>
                </Popover>
                )}
                {vehicleDetails.lastInspectionDate && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0 h-9"
                    onClick={() => {
                      update("lastInspectionDate", "");
                      setSelectedMonth(new Date().getMonth());
                      setSelectedYear(new Date().getFullYear());
                    }}
                  >
                    {ct.dialogs.verifyVehicleInfo.clear || "Rensa"}
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <AlertTriangle
              className={`${iconStyle} ${isDark ? "text-white" : "text-gray-900"}`}
            />
            <div className="flex flex-col min-w-0 flex-1 gap-1">
              <label className={`text-xs md:text-sm ${labelClass}`}>
                {ct.dialogs.verifyVehicleInfo.labels.accidentVehicle}
              </label>
              <Select
                value={crashProblem}
                onValueChange={(v: "yes" | "no" | "unknown") =>
                  setCrashProblem(v)
                }
              >
                <SelectTrigger className={inputClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unknown">
                    {ct.dialogs.verifyVehicleInfo.values.unknown}
                  </SelectItem>
                  <SelectItem value="no">
                    {ct.dialogs.verifyVehicleInfo.values.no}
                  </SelectItem>
                  <SelectItem value="yes">
                    {ct.dialogs.verifyVehicleInfo.values.yes}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Utrustning – samma popup som Lägg upp annons */}
      <div className="space-y-4">
        <h3 className="text-base md:text-xl font-semibold">
          {ct.dialogs.verifyVehicleInfo.equipment}
        </h3>
        <EquipmentSection
          equipment={equipment}
          vehicleType={vehicleDetails.vehicleType ?? "car"}
          onEquipmentSelect={setEquipment}
        />
      </div>

      <div className="flex justify-end pt-4 border-t border-border">
        <Button
          onClick={handleSave}
          disabled={isUpdatingTest}
          className="min-w-[160px]"
        >
          {isUpdatingTest ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {ct.dialogs.verifyVehicleInfo.saving}
            </>
          ) : (
            ct.dialogs.verifyVehicleInfo.saveChanges
          )}
        </Button>
      </div>

      <MakeSelectionDialog
        open={makeDialogOpen}
        onOpenChange={setMakeDialogOpen}
        selectedMake={vehicleDetails.make ?? ""}
        onSelect={handleMakeSelect}
        vehicleType={vehicleType}
      />
      <ModelSelectionDialog
        open={modelDialogOpen}
        onOpenChange={setModelDialogOpen}
        selectedMake={vehicleDetails.make ?? ""}
        selectedModel={vehicleDetails.model ?? ""}
        onSelect={(modelName) => {
          update("model", modelName);
          setModelDialogOpen(false);
        }}
        vehicleType={vehicleType}
      />
      <EngineSelectionDialog
        open={engineDialogOpen}
        onOpenChange={setEngineDialogOpen}
        selectedMake={vehicleDetails.make ?? ""}
        selectedEngine={vehicleDetails.engine ?? ""}
        onSelect={(engineName) => {
          update("engine", engineName);
          setEngineDialogOpen(false);
        }}
        vehicleType={vehicleType}
      />

      {/* Drivmedel val-dialog */}
      <Dialog open={fuelTypeDialogOpen} onOpenChange={setFuelTypeDialogOpen}>
        <DialogContent nested={isNative} className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Fuel className="h-5 w-5" />
              {ct.dialogs.verifyVehicleInfo.selectFuel}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 py-4">
            {MOCK_FUEL_TYPES.map((type) => (
              <Button
                key={type}
                onClick={() => {
                  update("fuelType", type);
                  setFuelTypeDialogOpen(false);
                }}
                variant="outline"
                className={`${
                  vehicleDetails.fuelType === type
                    ? "border-accent border-2 bg-accent/20"
                    : "border-gray-400"
                } truncate`}
              >
                {formatFuelType(type)}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Växellåda val-dialog */}
      <Dialog
        open={transmissionDialogOpen}
        onOpenChange={setTransmissionDialogOpen}
      >
        <DialogContent nested={isNative} className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              {ct.dialogs.verifyVehicleInfo.selectTransmission}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 py-4">
            {MOCK_TRANSMISSIONS.map((type) => (
              <Button
                key={type}
                onClick={() => {
                  update("transmission", type);
                  setTransmissionDialogOpen(false);
                }}
                variant="outline"
                className={`${
                  vehicleDetails.transmission === type
                    ? "border-accent border-2 bg-accent/20"
                    : "border-gray-400"
                } truncate`}
              >
                {formatTransmission(type)}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Chassityp val-dialog */}
      {vehicleType !== "mc" && (
        <Dialog open={bodyTypeDialogOpen} onOpenChange={setBodyTypeDialogOpen}>
          <DialogContent nested={isNative} className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Hash className="h-5 w-5" />
                {ct.dialogs.verifyVehicleInfo.selectBodyType}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 py-4">
              {MOCK_BODY_TYPES.map((type) => (
                <Button
                  key={type}
                  onClick={() => {
                    update("bodyType", type);
                    setBodyTypeDialogOpen(false);
                  }}
                  variant="outline"
                  className={`${
                    vehicleDetails.bodyType === type
                      ? "border-accent border-2 bg-accent/20"
                      : "border-gray-400"
                  } truncate`}
                >
                  {getBodyTypeLabel(type, language)}
                </Button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Färg val-dialog */}
      <Dialog open={colorDialogOpen} onOpenChange={setColorDialogOpen}>
        <DialogContent nested={isNative} className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              {ct.dialogs.verifyVehicleInfo.selectColor}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {(() => {
                const allColors = showAllColors
                  ? [...MOCK_COLORS, ...ADDITIONAL_COLORS]
                  : MOCK_COLORS;
                return allColors.map((type) => (
                  <Button
                    key={type}
                    onClick={() => {
                      update("color", type);
                      setShowColorOtherInput(false);
                      setColorOtherText("");
                      setColorDialogOpen(false);
                    }}
                    variant="outline"
                    className={`${
                      vehicleDetails.color === type
                        ? "border-accent border-2 bg-accent/20"
                        : "border-gray-400"
                    } truncate`}
                    title={getColorLabel(type, language)}
                  >
                    {getColorLabel(type, language)}
                  </Button>
                ));
              })()}
              {/* Övrigt option */}
              {!showColorOtherInput && (
                <Button
                  variant="outline"
                  onClick={handleColorOtherClick}
                  className={
                    vehicleDetails.color &&
                    !MOCK_COLORS.includes(vehicleDetails.color) &&
                    !ADDITIONAL_COLORS.includes(vehicleDetails.color)
                      ? "border-accent border-2 bg-accent/20"
                      : "border-gray-400"
                  }
                >
                  {ct.dialogs.verifyVehicleInfo.other}
                </Button>
              )}
            </div>
            {showColorOtherInput && (
              <div className="flex gap-2">
                <Input
                  value={colorOtherText}
                  onChange={(e) => setColorOtherText(e.target.value)}
                  placeholder={ct.dialogs.verifyVehicleInfo.enterColor}
                  maxLength={30}
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleColorOtherSubmit();
                    }
                  }}
                />
                <Button
                  onClick={handleColorOtherSubmit}
                  disabled={!colorOtherText.trim()}
                >
                  {ct.dialogs.verifyVehicleInfo.save}
                </Button>
              </div>
            )}
            <Button
              variant="ghost"
              className="w-full text-sm flex items-center justify-center gap-2"
              onClick={() => setShowAllColors(!showAllColors)}
            >
              {showAllColors
                ? ct.dialogs.verifyVehicleInfo.showLess
                : ct.dialogs.verifyVehicleInfo.showMore}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  showAllColors ? "rotate-180" : ""
                }`}
              />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
